import {
  CampoDeFiltro,
  GrupoDeFiltro,
  OpcaoDeFiltro,
  type FiltroMarcado,
} from '../../componentes/ui';
import { ETAPAS, ROTULO_DO_TIPO } from './comum';
import { FILTRO_DO_PONTUAL_VAZIO, type EscolhasDoPontual } from './filtro';
import type { EtapaDaNegociacao, Negociacao } from './tipos';

const TIPOS = Object.entries(ROTULO_DO_TIPO).map(([chave, rotulo]) => ({ chave, rotulo }));

/**
 * O "Filtro" das negociações do Pontual: a etapa do ciclo (em simulação,
 * contrato em assinatura… paga) e o tipo de crédito. O campo e o painel são os
 * do kit (`CampoDeFiltro`), como na Prospecção e em Empresas.
 *
 * Ao lado de cada opção, quantas negociações há nela — contadas na lista da
 * busca, para "quantas da CLARO estão a receber" sair sem marcar nada.
 */
export function FiltroDoPontual(props: {
  readonly escolhas: EscolhasDoPontual;
  readonly aoMudar: (escolhas: EscolhasDoPontual) => void;
  /** A lista em que as contagens são feitas — a da busca. */
  readonly base: readonly Negociacao[];
  readonly className?: string;
}): JSX.Element {
  const { escolhas, aoMudar, base } = props;

  // Na ordem da lista, sempre: a etiqueta não depende da ordem do clique.
  const alternarEtapa = (e: EtapaDaNegociacao): void =>
    aoMudar({
      ...escolhas,
      etapas: escolhas.etapas.includes(e)
        ? escolhas.etapas.filter((x) => x !== e)
        : ETAPAS.map((x) => x.chave).filter((x) => x === e || escolhas.etapas.includes(x)),
    });
  const alternarTipo = (t: string): void =>
    aoMudar({
      ...escolhas,
      tipos: escolhas.tipos.includes(t)
        ? escolhas.tipos.filter((x) => x !== t)
        : TIPOS.map((x) => x.chave).filter((x) => x === t || escolhas.tipos.includes(x)),
    });

  const marcados: FiltroMarcado[] = [
    ...ETAPAS.filter((e) => escolhas.etapas.includes(e.chave)).map((e) => ({
      rotulo: e.rotulo,
      tirar: () => alternarEtapa(e.chave),
    })),
    ...TIPOS.filter((t) => escolhas.tipos.includes(t.chave)).map((t) => ({
      rotulo: t.rotulo,
      tirar: () => alternarTipo(t.chave),
    })),
  ];

  return (
    <CampoDeFiltro
      marcados={marcados}
      semFiltro="Todas as negociações"
      aoLimpar={() => aoMudar(FILTRO_DO_PONTUAL_VAZIO)}
      {...(props.className === undefined ? {} : { className: props.className })}
    >
      <GrupoDeFiltro
        titulo="Etapa"
        nota="Marcando mais de uma, entra a negociação em qualquer uma delas."
      >
        {ETAPAS.map((e) => (
          <OpcaoDeFiltro
            key={e.chave}
            rotulo={e.rotulo}
            marcada={escolhas.etapas.includes(e.chave)}
            aoMudar={() => alternarEtapa(e.chave)}
            contagem={base.filter((n) => n.proximaAcao.etapa === e.chave).length}
          />
        ))}
      </GrupoDeFiltro>

      <GrupoDeFiltro titulo="Tipo de crédito">
        {TIPOS.map((t) => (
          <OpcaoDeFiltro
            key={t.chave}
            rotulo={t.rotulo}
            marcada={escolhas.tipos.includes(t.chave)}
            aoMudar={() => alternarTipo(t.chave)}
            contagem={base.filter((n) => n.tipoCredito === t.chave).length}
          />
        ))}
      </GrupoDeFiltro>
    </CampoDeFiltro>
  );
}
